/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import Retracked from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import CsrfTokenContext from 'bundles/page/contexts/CsrfTokenContext';
import RouterContext from 'bundles/page/contexts/RouterContext';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import 'css!bundles/partners-graphql/styl/all';

const styles = {
  wrapperStyles: css`
    .max-width-980.m-t-3 {
      margin: 48px auto 0;
      max-width: 980px;
    }
  `,
};

type PropsFromStores = {
  csrfToken?: string | null;
};

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsToComponent = PropsFromStores & PropsFromCaller;

const PartnerAppContent = ({ children }: React.PropsWithChildren) => {
  return (
    <div className="rc-PartnerApp">
      <DefaultHeader showMetaNav />
      <UniversalPageFeatures />
      {children}
      <PageFooter />
    </div>
  );
};

const PartnerApp = ({ children, csrfToken }: PropsToComponent) => {
  const router = useRouter();

  return (
    <StatsigProvider>
      <div css={styles.wrapperStyles}>
        <RouterContext.Provider value={router}>
          <CsrfTokenContext.Provider value={csrfToken || '<MISSING>'}>
            <PartnerAppContent>{children}</PartnerAppContent>
          </CsrfTokenContext.Provider>
        </RouterContext.Provider>
      </div>
    </StatsigProvider>
  );
};

export default compose<PropsToComponent, PropsFromStores>(
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'partners',
      },
    };
  }),
  connectToStores<PropsFromStores, PropsFromCaller, ApplicationStoreClass>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      csrfToken: ApplicationStore.getState().csrfToken,
    })
  )
)(PartnerApp);
